import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { addMaterial, uploadMaterialFile } from '@/api/materialInfo';
import { getTree } from '@/api/materialCategory';
import type { CategoryNode } from '@/api/materialCategory';

type UploadForm = {
  title: string;
  description: string;
  category: string;
  modelCategory: string;
  type: 'PDF' | 'PPT' | 'Video' | 'Excel' | 'Word';
  downloadAllowed: boolean;
  downloadWatermark: boolean;
  confidential: boolean;
  videoDuration: string;
  fileUrl: string;
  tags: string;
};

const defaultForm: UploadForm = {
  title: '',
  description: '',
  category: '',
  modelCategory: '',
  type: 'PDF',
  downloadAllowed: false,
  downloadWatermark: true,
  confidential: false,
  videoDuration: '',
  fileUrl: '',
  tags: '',
};

/** 根据文件扩展名推断文件类型 */
function detectFileType(fileName: string): UploadForm['type'] {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const videoExts = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'wmv', 'flv'];
  if (videoExts.includes(ext)) return 'Video';
  if (ext === 'pdf') return 'PDF';
  if (ext === 'ppt' || ext === 'pptx') return 'PPT';
  if (ext === 'xls' || ext === 'xlsx') return 'Excel';
  if (ext === 'doc' || ext === 'docx') return 'Word';
  return 'PDF'; // 默认回退
}

function getModelsForSubCat(tree: CategoryNode[], subCatId: string) {
  for (const primary of tree) {
    const sub = primary.subCategories.find((s) => s.id === subCatId);
    if (sub) return sub.models;
  }
  return [];
}

function getSubCategoryLabel(tree: CategoryNode[], subCatId: string): string {
  for (const primary of tree) {
    const sub = primary.subCategories.find((s) => s.id === subCatId);
    if (sub) return sub.label;
  }
  return subCatId;
}



export default function MaterialUploadPage() {
  const navigate = useNavigate();
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [selectedPrimary, setSelectedPrimary] = useState<string>('');
  const [form, setForm] = useState<UploadForm>(defaultForm);
  const [dragging, setDragging] = useState(false);
  const [videoDragging, setVideoDragging] = useState(false);
  const [fileLabel, setFileLabel] = useState<string | null>(null);
  const [videoFileLabel, setVideoFileLabel] = useState<string | null>(null);
  const [coverLabel, setCoverLabel] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [selectedCoverFile, setSelectedCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTree();
        setTree(data);
      } catch (err) {
        console.error('加载分类树失败', err);
      }
    })();
  }, []);

  // 封面图预览 URL 管理
  useEffect(() => {
    if (selectedCoverFile) {
      const url = URL.createObjectURL(selectedCoverFile);
      setCoverPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setCoverPreviewUrl(null);
  }, [selectedCoverFile]);

  const isElearning = form.category === 'E-learning';
  const models = getModelsForSubCat(tree, form.category);
  const primaryCat = tree.find((p) => p.id === selectedPrimary);
  const subCategoryLabel = getSubCategoryLabel(tree, form.category);
  const selectedModelLabel = form.modelCategory ? models.find((m) => m.id === form.modelCategory)?.label ?? form.modelCategory : '';

  const handleCategoryChange = (cat: string) => {
    const isVideo = cat === 'E-learning';
    setForm((prev) => ({
      ...prev,
      category: cat,
      modelCategory: '',
      type: isVideo ? 'Video' : prev.type === 'Video' ? 'PDF' : prev.type,
      downloadAllowed: isVideo ? false : prev.downloadAllowed,
    }));
    setFileLabel(null);
    setVideoFileLabel(null);
    setSelectedFile(null);
    setSelectedVideoFile(null);
    setCoverLabel(null);
    setSelectedCoverFile(null);
  };

  const handlePrimaryChange = (primaryId: string) => {
    setSelectedPrimary(primaryId);
    setForm((prev) => ({
      ...prev,
      category: '',
      modelCategory: '',
    }));
    setFileLabel(null);
    setVideoFileLabel(null);
    setSelectedFile(null);
    setSelectedVideoFile(null);
    setCoverLabel(null);
    setSelectedCoverFile(null);
  };

  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    try {
      setSubmitting(true);
      const fileToUpload = isElearning ? selectedVideoFile : selectedFile;
      if (!fileToUpload) return;
      const uploadResult = await uploadMaterialFile(fileToUpload);

      // 上传封面图（如果选择了）
      let thumbnailUrl = '';
      if (selectedCoverFile) {
        const coverResult = await uploadMaterialFile(selectedCoverFile);
        thumbnailUrl = coverResult.url;
      }

      const sizeKB = Math.round(uploadResult.size / 1024);
      const fileSizeStr = sizeKB >= 1024
        ? `${(sizeKB / 1024).toFixed(2)} MB`
        : `${sizeKB} KB`;
      await addMaterial({
        title: form.title,
        type: form.type,
        category: form.category,
        primaryCategory: selectedPrimary,
        modelCategory: form.modelCategory,
        description: form.description,
        tags: form.tags,
        fileUrl: uploadResult.url,
        videoDuration: form.videoDuration,
        downloadAllowed: form.downloadAllowed,
        downloadWatermark: form.downloadWatermark,
        confidential: form.confidential,
        isVideo: form.type === 'Video',
        fileSize: fileSizeStr,
        thumbnail: thumbnailUrl,
      });
      setSubmitted(true);
      setTimeout(() => {
        navigate('/admin/materials');
      }, 1200);
    } catch (err) {
      console.error('上传资料失败', err);
    } finally {
      setSubmitting(false);
    }
  };


  const hasFile = isElearning ? !!selectedVideoFile : !!selectedFile;
  const canSubmit = form.title.trim() !== '' && form.category !== '' && hasFile;

  if (submitted) {
    return (
      <AdminLayout title="上传资料" subtitle="新增资料到资料库">
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <i className="ri-checkbox-circle-fill text-3xl text-green-500"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">上传成功！</h2>
          <p className="text-sm text-gray-500">正在返回资料列表...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="上传新资料" subtitle="填写资料信息并上传文件">
      {/* Back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/materials')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base"></i>
          返回资料列表
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Preview card */}
          <div className="lg:col-span-1 space-y-4">
            {/* Preview card */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">资料预览</h3>
              <div className="space-y-3">
                {/* Thumbnail placeholder */}
                <div className="w-full rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden" style={{ aspectRatio: '218/121' }}>
                  {coverPreviewUrl ? (
                    <img
                      src={coverPreviewUrl}
                      alt="封面预览"
                      className="w-full h-full object-cover"
                    />
                  ) : coverLabel ? (
                    <div className="flex flex-col items-center gap-1">
                      <i className="ri-image-line text-2xl text-gray-400"></i>
                      <span className="text-xs text-gray-500 truncate max-w-[140px]">{coverLabel}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-300">
                      {isElearning ? (
                        <i className="ri-video-line text-3xl"></i>
                      ) : (
                        <i className="ri-file-line text-3xl"></i>
                      )}
                      <span className="text-xs">封面预览</span>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <p className="text-sm font-bold text-gray-900 leading-snug">
                    {form.title || <span className="text-gray-300 font-normal">资料标题</span>}
                  </p>
                </div>

                {/* Category breadcrumb */}
                {form.category && (
                  <div className="flex items-center gap-1 flex-wrap">
                    {primaryCat && (
                      <>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: `${primaryCat.color}15`, color: primaryCat.color }}>
                          <i className={`${primaryCat.icon} text-xs`}></i>
                          {primaryCat.label}
                        </span>
                        <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
                      </>
                    )}
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      {subCategoryLabel}
                    </span>
                    {form.modelCategory && (
                      <>
                        <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
                          <i className="ri-cpu-line text-xs"></i>
                          {selectedModelLabel}
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* Badges */}
                <div className="flex flex-wrap gap-1.5">
                  {form.type && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border bg-gray-50 text-gray-700 border-gray-200">
                      <i className={`${form.type === 'PDF' ? 'ri-file-pdf-line' : form.type === 'Video' ? 'ri-video-line' : form.type === 'PPT' ? 'ri-presentation-line' : form.type === 'Excel' ? 'ri-file-excel-line' : 'ri-file-word-line'} text-xs`}></i>
                      {form.type}
                    </span>
                  )}
                  {form.confidential ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-red-50 text-red-600 border-red-200">
                      <i className="ri-shield-keyhole-line text-xs"></i>机密
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-50 text-gray-500 border-gray-200">
                      <i className="ri-global-line text-xs"></i>普通
                    </span>
                  )}
                  {form.downloadAllowed && !isElearning && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-green-50 text-green-600 border-green-200">
                      <i className="ri-download-line text-xs"></i>可下载
                    </span>
                  )}
                </div>

                {/* Description */}
                {form.description && (
                  <p className="text-xs text-gray-400 leading-relaxed line-clamp-3">{form.description}</p>
                )}

                {/* File info */}
                {(fileLabel || videoFileLabel) && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-100">
                    <i className="ri-checkbox-circle-fill text-green-500 text-sm shrink-0"></i>
                    <span className="text-xs text-green-700 font-medium truncate">{fileLabel || videoFileLabel}</span>
                  </div>
                )}
              </div>

              {/* Tips */}
              <div className="mt-5 pt-4 border-t border-gray-100 space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">上传须知</p>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <i className="ri-eye-line text-xs text-gray-400 mt-0.5 shrink-0"></i>
                    <p className="text-xs text-gray-400">所有格式在线预览时均叠加水印，不可关闭</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-file-pdf-line text-xs text-gray-400 mt-0.5 shrink-0"></i>
                    <p className="text-xs text-gray-400">下载水印仅支持 PDF 格式，Word / Excel / PPT 下载后为原始文件</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-lock-line text-xs text-gray-400 mt-0.5 shrink-0"></i>
                    <p className="text-xs text-gray-400">E-learning 视频禁止下载，仅在线播放</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-information-line text-xs text-gray-400 mt-0.5 shrink-0"></i>
                    <p className="text-xs text-gray-400">机密资料上传后需手动授权代理商</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-2 space-y-5">
            {/* Section: 基本信息 */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center rounded bg-gray-100">
                  <i className="ri-file-info-line text-xs text-gray-500"></i>
                </div>
                基本信息
              </h3>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">标题 *</label>
                  <input
                    required
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="资料标题，清晰描述内容"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">描述</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="简要描述资料内容、适用场景..."
                    rows={3}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 resize-none"
                  />
                </div>

                {/* Tags — 暂时隐藏 */}
                {/* <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                    标签
                    <span className="ml-1 text-gray-400 font-normal normal-case">（逗号分隔）</span>
                  </label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    placeholder="如：CT Systems, 产品手册, 2024"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                  />
                </div> */}
              </div>
            </div>

            {/* Section: 分类设置 */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center rounded bg-gray-100">
                  <i className="ri-folder-3-line text-xs text-gray-500"></i>
                </div>
                分类设置
              </h3>
              <div className="space-y-4">
                {/* 一级分类 */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">一级分类 *</label>
                  <div className="relative">
                    <select
                      required
                      value={selectedPrimary}
                      onChange={(e) => handlePrimaryChange(e.target.value)}
                      className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer"
                    >
                      <option value="">选择一级分类</option>
                      {tree.map((primary) => (
                        <option key={primary.id} value={primary.id}>{primary.label}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </div>
                  </div>
                </div>

                {/* 二级分类 */}
                {selectedPrimary && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">二级分类 *</label>
                    <div className="relative">
                      <select
                        required
                        value={form.category}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer"
                      >
                        <option value="">选择二级分类</option>
                        {tree.find((p) => p.id === selectedPrimary)?.subCategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>{sub.label}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </div>
                    </div>
                  </div>
                )}

                {/* 三级分类（产品型号） */}
                {form.category && models.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                      三级分类（产品型号）
                      <span className="ml-1 text-gray-400 font-normal normal-case">（选填，精确到具体型号）</span>
                    </label>
                    <div className="relative">
                      <select
                        value={form.modelCategory}
                        onChange={(e) => setForm({ ...form, modelCategory: e.target.value })}
                        className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer"
                      >
                        <option value="">不指定型号（适用全系列）</option>
                        {models.map((model) => (
                          <option key={model.id} value={model.id}>{model.label}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </div>
                    </div>
                    {form.modelCategory && (
                      <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
                        <i className="ri-cpu-line text-xs"></i>
                        此资料将归属于型号：<strong>{selectedModelLabel}</strong>
                      </p>
                    )}
                  </div>
                )}

                {/* 当前选择路径提示（面包屑） */}
                {form.category && primaryCat && (
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <i className={`${primaryCat.icon} text-xs`} style={{ color: primaryCat.color }}></i>
                    <span style={{ color: primaryCat.color }} className="font-medium">{primaryCat.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300"></i>
                    <span className="text-gray-600 font-semibold">{subCategoryLabel}</span>
                    {form.modelCategory && (
                      <>
                        <i className="ri-arrow-right-s-line text-gray-300"></i>
                        <span className="text-gray-600 font-medium">{selectedModelLabel}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Section: 文件类型与权限 */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center rounded bg-gray-100">
                  <i className="ri-settings-3-line text-xs text-gray-500"></i>
                </div>
                文件类型与权限
              </h3>
              <div className="space-y-4">
                {/* File type */}
                {!isElearning && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">文件类型</label>
                    <div className="relative">
                      <select
                        value={form.type}
                        onChange={(e) => setForm({ ...form, type: e.target.value as UploadForm['type'] })}
                        className="w-full appearance-none pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400 bg-white cursor-pointer"
                      >
                        <option value="PDF">PDF</option>
                        <option value="Video">Video（视频）</option>
                        <option value="PPT">PPT</option>
                        <option value="Excel">Excel</option>
                        <option value="Word">Word</option>
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </div>
                    </div>
                    <div className="mt-2 flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                      <i className="ri-information-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        仅支持 PDF、Video、PPT、Excel、Word 格式。其他格式建议先转换为 <strong>PDF</strong> 后再上传。
                      </p>
                    </div>
                  </div>
                )}




                {/* Watermark hints — 根据文件类型显示对应说明 */}
                {form.type === 'PDF' && !isElearning && (
                  <div className="rounded-lg border border-amber-100 bg-amber-50/50 overflow-hidden">
                    <div className="px-3 py-2.5 space-y-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-eye-line text-amber-500 text-xs shrink-0"></i>
                        <p className="text-xs text-amber-700"><strong>在线预览：</strong>始终叠加动态水印，不可关闭</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-download-line text-amber-500 text-xs shrink-0"></i>
                        <p className="text-xs text-amber-700"><strong>下载水印：</strong>PDF 支持嵌入水印，可在下方开关控制</p>
                      </div>
                    </div>
                  </div>
                )}
                {form.type === 'PPT' && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden">
                    <div className="px-3 py-2.5 space-y-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-eye-line text-gray-500 text-xs shrink-0"></i>
                        <p className="text-xs text-gray-600"><strong>在线预览：</strong>始终叠加动态水印，不可关闭</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-information-line text-gray-400 text-xs shrink-0"></i>
                        <p className="text-xs text-gray-500"><strong>下载水印：</strong>PPT 文件下载后为原始格式，系统无法嵌入水印</p>
                      </div>
                    </div>
                  </div>
                )}
                {(form.type === 'Excel' || form.type === 'Word') && !isElearning && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden">
                    <div className="px-3 py-2.5 space-y-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-eye-line text-gray-500 text-xs shrink-0"></i>
                        <p className="text-xs text-gray-600"><strong>在线预览：</strong>始终叠加动态水印，不可关闭</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-information-line text-gray-400 text-xs shrink-0"></i>
                        <p className="text-xs text-gray-500"><strong>下载水印：</strong>{form.type} 文件下载后为原始格式，系统无法嵌入水印</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Download toggle */}
                {!isElearning && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50/50">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200">
                      <i className="ri-download-line text-sm text-gray-500"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">允许下载</p>
                      <p className="text-xs text-gray-400">代理商可将此资料下载到本地</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, downloadAllowed: !form.downloadAllowed })}
                      className="w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0"
                      style={{ background: form.downloadAllowed ? '#0069BD' : '#d1d5db' }}
                    >
                      <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-200" style={{ left: form.downloadAllowed ? '22px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}></span>
                    </button>
                  </div>
                )}

                {/* Download watermark toggle — 仅 PDF 支持 */}
                {!isElearning && form.type === 'PDF' && (
                  <div className={`rounded-xl border p-4 transition-all ${form.downloadWatermark ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200 bg-gray-50/40'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${form.downloadWatermark ? 'bg-amber-100' : 'bg-gray-100'}`}>
                          <i className={`ri-mark-pen-line text-sm ${form.downloadWatermark ? 'text-amber-600' : 'text-gray-400'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">下载时叠加水印</p>
                          <p className="text-xs text-gray-400">PDF 下载文件中嵌入水印（仅 PDF 支持）</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, downloadWatermark: !form.downloadWatermark })}
                        className="w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0"
                        style={{ background: form.downloadWatermark ? '#f59e0b' : '#d1d5db' }}
                      >
                        <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-200" style={{ left: form.downloadWatermark ? '22px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}></span>
                      </button>
                    </div>
                    {!form.downloadWatermark && (
                      <p className="text-xs text-gray-500 mt-2.5 flex items-center gap-1.5">
                        <i className="ri-information-line text-gray-400"></i>
                        已关闭下载水印，适合本地印刷或对外宣传使用
                      </p>
                    )}
                  </div>
                )}

                {/* Confidential toggle */}
                <div className={`rounded-xl border p-4 transition-all ${form.confidential ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-gray-50/40'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${form.confidential ? 'bg-red-100' : 'bg-gray-100'}`}>
                        <i className={`ri-shield-keyhole-line text-sm ${form.confidential ? 'text-red-500' : 'text-gray-400'}`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">设为机密资料</p>
                        <p className="text-xs text-gray-400">开启后仅被授权的代理商可查看</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, confidential: !form.confidential })}
                      className="w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0"
                      style={{ background: form.confidential ? '#ef4444' : '#d1d5db' }}
                    >
                      <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-200" style={{ left: form.confidential ? '22px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}></span>
                    </button>
                  </div>
                  {form.confidential && (
                    <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5">
                      <i className="ri-information-line"></i>
                      上传后请在资料列表中点击「机密权限」为代理商授权
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section: 文件上传 */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center rounded bg-gray-100">
                  <i className="ri-upload-cloud-line text-xs text-gray-500"></i>
                </div>
                文件上传 <span className="text-red-400">*</span>
              </h3>

              {isElearning ? (
                <div className="space-y-4">
                  {/* Video URL */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">外部视频链接</label>
                    <input
                      type="url"
                      value={form.fileUrl}
                      onChange={(e) => setForm({ ...form, fileUrl: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-400 font-medium">或上传视频文件</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                  {/* Video upload */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setVideoDragging(true); }}
                    onDragLeave={() => setVideoDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setVideoDragging(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) { setVideoFileLabel(file.name); setSelectedVideoFile(file); setForm((prev) => ({ ...prev, type: detectFileType(file.name) })); }
                    }}
                    onClick={() => videoInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${videoDragging ? 'border-violet-400 bg-violet-50' : 'border-violet-200 hover:border-violet-400 hover:bg-violet-50/60'}`}
                  >
                    {videoFileLabel ? (
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-checkbox-circle-fill text-violet-500 text-lg"></i>
                        <span className="text-sm font-semibold text-violet-700 truncate max-w-[300px]">{videoFileLabel}</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setVideoFileLabel(null); setSelectedVideoFile(null); }} className="w-5 h-5 flex items-center justify-center rounded-full bg-violet-100 text-violet-500 hover:bg-violet-200 cursor-pointer shrink-0">
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-violet-100 mx-auto mb-3">
                          <i className="ri-upload-cloud-2-line text-2xl text-violet-500"></i>
                        </div>
                        <p className="text-sm font-semibold text-gray-700">拖拽视频至此，或点击选择</p>
                        <p className="text-xs text-gray-400 mt-1">支持 MP4, MOV, WebM</p>
                      </>
                    )}
                    <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setVideoFileLabel(f.name); setSelectedVideoFile(f); setForm((prev) => ({ ...prev, type: detectFileType(f.name) })); } }} />
                  </div>
                  {/* Duration */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">时长（选填）</label>
                    <input
                      type="text"
                      value={form.videoDuration}
                      onChange={(e) => setForm({ ...form, videoDuration: e.target.value })}
                      placeholder="如：18:42"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                    />
                  </div>
                  {/* Cover image */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">封面图（选填）</label>
                    <div
                      onClick={() => coverInputRef.current?.click()}
                      className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                    >
                      {coverLabel ? (
                        <div className="flex items-center justify-center gap-2">
                          <i className="ri-checkbox-circle-fill text-green-500 text-base"></i>
                          <span className="text-sm font-semibold text-gray-700 truncate max-w-[300px]">{coverLabel}</span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setCoverLabel(null); setSelectedCoverFile(null); }} className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-pointer shrink-0">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <i className="ri-image-line text-2xl text-gray-400 mb-1 block"></i>
                          <p className="text-sm text-gray-600">上传封面图片</p>
                          <p className="text-xs text-gray-400 mt-0.5">建议 JPG、PNG，16:9 比例</p>
                        </>
                      )}
                      <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setCoverLabel(f.name); setSelectedCoverFile(f); } }} />
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                    <i className="ri-lock-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                    <p className="text-xs text-amber-700">E-learning 视频下载自动禁用，只允许在线播放，在线播放时叠加水印。</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* File upload */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragging(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) { setFileLabel(file.name); setSelectedFile(file); setForm((prev) => ({ ...prev, type: detectFileType(file.name) })); }
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${dragging ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}
                  >
                    {fileLabel ? (
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-checkbox-circle-fill text-green-500 text-lg"></i>
                        <span className="text-sm font-semibold text-gray-700 truncate max-w-[300px]">{fileLabel}</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setFileLabel(null); setSelectedFile(null); }} className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-pointer shrink-0">
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
                          <i className="ri-upload-cloud-2-line text-2xl text-gray-500"></i>
                        </div>
                        <p className="text-sm font-semibold text-gray-700">拖拽文件至此，或点击选择</p>
                        <p className="text-xs text-gray-400 mt-1">支持 PDF、Video、PPT、Excel、Word</p>
                      </>
                    )}
                    <input ref={fileInputRef} type="file" accept=".pdf,.ppt,.pptx,.xls,.xlsx,.doc,.docx,video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFileLabel(f.name); setSelectedFile(f); setForm((prev) => ({ ...prev, type: detectFileType(f.name) })); } }} />
                  </div>
                  {/* 视频时长（非E-learning的Video类型） */}
                  {form.type === 'Video' && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">时长（选填）</label>
                      <input
                        type="text"
                        value={form.videoDuration}
                        onChange={(e) => setForm({ ...form, videoDuration: e.target.value })}
                        placeholder="如：18:42"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-gray-400"
                      />
                    </div>
                  )}
                  {/* 封面图 */}
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">封面图（选填）</label>
                    <div
                      onClick={() => coverInputRef.current?.click()}
                      className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                    >
                      {coverLabel ? (
                        <div className="flex items-center justify-center gap-2">
                          <i className="ri-checkbox-circle-fill text-green-500 text-base"></i>
                          <span className="text-sm font-semibold text-gray-700 truncate max-w-[300px]">{coverLabel}</span>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setCoverLabel(null); setSelectedCoverFile(null); }} className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 cursor-pointer shrink-0">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <i className="ri-image-line text-2xl text-gray-400 mb-1 block"></i>
                          <p className="text-sm text-gray-600">上传封面图片</p>
                          <p className="text-xs text-gray-400 mt-0.5">建议 JPG、PNG，16:9 比例</p>
                        </>
                      )}
                      <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setCoverLabel(f.name); setSelectedCoverFile(f); } }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-3 pb-6">
              <button
                type="button"
                onClick={() => navigate('/admin/materials')}
                className="flex-1 py-3 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="flex-1 py-3 text-white rounded-lg text-sm font-semibold hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                style={{ background: '#0069BD' }}
              >
                <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'} mr-1.5`}></i>
                {submitting ? '上传中...' : '确认上传'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
